import { isAxiosError } from "axios";
import { privateAxios } from "../axiosInstances";
import { EventFormQuestionForm, OrganiserQuestionForm } from "../schemas";
import { DeleteOrganiserQuestionResponse, EventFormQuestion } from "@ac-expo/api-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { eventFormQuestionsQueryKeys } from "../query-keys/event-form-questions.query-keys";
import { EventFormQuestionsOnEventForm } from "../schemas/event-form-questions/set-event-form-questions-on-event";
import { eventFormAnswersQueryKeys } from "../query-keys";

const createEventFormQuestion = async ({
  data,
}: {
  data: EventFormQuestionForm;
}) => {
  try {
    const { data: response } = await privateAxios.post(
      `/event-form-questions`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useCreateEventFormQuestionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createEventFormQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.all,
      });
    },
  });
};

const updateEventFormQuestion = async ({
  id,
  data,
}: {
  id: string;
  data: EventFormQuestionForm;
}) => {
  try {
    const { data: response } = await privateAxios.put(
      `/event-form-questions/${id}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateEventFormQuestionMutation = (id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: EventFormQuestionForm }) =>
      updateEventFormQuestion({ id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.all,
      });
    },
  });
};

const deleteEventFormQuestion = async (id: string) => {
  try {
    const { data: response } = await privateAxios.delete(
      `/event-form-questions/${id}`
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useDeleteEventFormQuestionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteEventFormQuestion,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.all,
      });
    },
  });
};

const setEventFormQuestionsOnEvent = async ({
  eventId,
  data,
}: {
  eventId: string;
  data: EventFormQuestionsOnEventForm;
}) => {
  try {
    const { data: response } = await privateAxios.post(
      `/event-form-questions/${eventId}/questions`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useSetEventFormQuestionsOnEventMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ data }: { data: EventFormQuestionsOnEventForm }) =>
      setEventFormQuestionsOnEvent({ eventId, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.onEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.cashRegisterForm(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventFormAnswersQueryKeys.all,
      });
    },
  });
};

const setCashRegisterFormQuestions = async ({
  eventId,
  questionIds,
}: {
  eventId: string;
  questionIds: string[];
}) => {
  try {
    const { data: response } = await privateAxios.put(
      `/event-form-questions/${eventId}/cash-register-questions`,
      { questionIds }
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useSetCashRegisterFormQuestionsMutation = (eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ questionIds }: { questionIds: string[] }) =>
      setCashRegisterFormQuestions({ eventId, questionIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.cashRegisterForm(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventFormQuestionsQueryKeys.onEvent(eventId),
      });
      queryClient.invalidateQueries({
        queryKey: eventFormAnswersQueryKeys.all,
      });
    },
  });
};

const organiserQuestionsUrl = (eventId: string) =>
  `/event-form-questions/${eventId}/cash-register-questions/organiser`;

// Organiser questions change the pool, the saved list and the answers columns
const useInvalidateCashRegisterForm = (eventId: string) => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: eventFormQuestionsQueryKeys.cashRegisterForm(eventId),
    });
    queryClient.invalidateQueries({
      queryKey: eventFormQuestionsQueryKeys.onEvent(eventId),
    });
    queryClient.invalidateQueries({
      queryKey: eventFormAnswersQueryKeys.all,
    });
  };
};

const createOrganiserQuestion = async ({
  eventId,
  data,
}: {
  eventId: string;
  data: OrganiserQuestionForm;
}) => {
  try {
    const { data: response } = await privateAxios.post<EventFormQuestion>(
      organiserQuestionsUrl(eventId),
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useCreateOrganiserQuestionMutation = (eventId: string) => {
  const invalidate = useInvalidateCashRegisterForm(eventId);

  return useMutation({
    mutationFn: ({ data }: { data: OrganiserQuestionForm }) =>
      createOrganiserQuestion({ eventId, data }),
    onSuccess: invalidate,
  });
};

const updateOrganiserQuestion = async ({
  eventId,
  questionId,
  data,
}: {
  eventId: string;
  questionId: string;
  data: OrganiserQuestionForm;
}) => {
  try {
    const { data: response } = await privateAxios.put<EventFormQuestion>(
      `${organiserQuestionsUrl(eventId)}/${questionId}`,
      data
    );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useUpdateOrganiserQuestionMutation = (eventId: string) => {
  const invalidate = useInvalidateCashRegisterForm(eventId);

  return useMutation({
    mutationFn: ({
      questionId,
      data,
    }: {
      questionId: string;
      data: OrganiserQuestionForm;
    }) => updateOrganiserQuestion({ eventId, questionId, data }),
    onSuccess: invalidate,
  });
};

const deleteOrganiserQuestion = async ({
  eventId,
  questionId,
}: {
  eventId: string;
  questionId: string;
}) => {
  try {
    const { data: response } =
      await privateAxios.delete<DeleteOrganiserQuestionResponse>(
        `${organiserQuestionsUrl(eventId)}/${questionId}`
      );
    return response;
  } catch (error) {
    if (isAxiosError(error))
      error.message = error.response?.data.message ?? error.message;
    throw error;
  }
};

export const useDeleteOrganiserQuestionMutation = (eventId: string) => {
  const invalidate = useInvalidateCashRegisterForm(eventId);

  return useMutation({
    mutationFn: ({ questionId }: { questionId: string }) =>
      deleteOrganiserQuestion({ eventId, questionId }),
    onSuccess: invalidate,
  });
};
